// Pieces shared by the planner's edit pages: going back, the "Outros nomes" editor and the people chips.
import React, { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as T from './types';
import { avatarColor, withPendingAlias } from './labels';

/**
 * Back in history when the page was reached from inside the app (keeps the list's search), else to
 * `fallback`, replacing the page so the browser's back does not return to it.
 */
export function useGoBack(fallback: string) {
  const navigate = useNavigate();
  return () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
}

/**
 * "Outros nomes": the names something answers to by voice; chips with ✕ and a field to add more.
 * When the page holds the typed text (`pending`), it counts it when saving (`withPendingAlias`);
 * otherwise the text is added when the field loses focus, so it is not lost.
 */
export function AliasEditor({ value, onChange, pending, onPendingChange, disabled }: {
  value: string[]; onChange: (aliases: string[]) => void;
  pending?: string; onPendingChange?: (text: string) => void; disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const controlled = pending !== undefined;
  const text = controlled ? pending : draft;
  const setText = (next: string) => (controlled ? onPendingChange?.(next) : setDraft(next));

  const add = () => {
    if (!text.trim()) return;
    const next = withPendingAlias(value, text);
    if (next !== value) onChange(next);
    setText('');
  };

  return (
    <Stack spacing={1}>
      {value.length === 0
        ? <Typography variant="caption" color="text.secondary">Ainda nenhum. O assistente junta aqui os nomes que aprende.</Typography>
        : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {value.map(a => (
              <Chip key={a} label={a} color="primary" variant="outlined" onDelete={disabled ? undefined : () => onChange(value.filter(x => x !== a))} />
            ))}
          </Box>
        )}
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder="Outro nome (ex.: «massa da avó»)"
          value={text}
          disabled={disabled}
          onChange={e => setText(e.target.value)}
          onBlur={controlled ? undefined : add}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button variant="outlined" onClick={add} disabled={disabled}>Juntar</Button>
      </Stack>
    </Stack>
  );
}

/** Tap to tick/untick people, in their avatar colour. */
export function MemberChips({ members, selected, onToggle }: {
  members: T.HouseholdMember[]; selected: T.Guid[]; onToggle: (id: T.Guid) => void;
}) {
  if (members.length === 0) return <Typography variant="body2" color="text.secondary">Ainda não há pessoas na casa.</Typography>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {members.map(m => {
        const on = selected.includes(m.publicId);
        const color = avatarColor(m);
        return (
          <Chip
            key={m.publicId}
            label={m.name.split(' ')[0]}
            onClick={() => onToggle(m.publicId)}
            variant={on ? 'filled' : 'outlined'}
            aria-pressed={on}
            sx={{ borderColor: color, ...(on ? { bgcolor: color, color: '#fff', '&:hover': { bgcolor: color, opacity: 0.9 } } : {}) }}
          />
        );
      })}
    </Box>
  );
}
